
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import { useUserRole } from "@/hooks/useUserRole";

interface ProfileData {
  id: string;
  avatar_url: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  is_accredited: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface ProfilePictureMutationContext {
  previousData: ProfileData | undefined;
  queryKey: readonly unknown[];
}

const COMPONENT = 'ProfilePictureMutation';

export function useProfilePictureMutation(userId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['profile-data', userId] as const;
  const { userRole, hasRole } = useUserRole();

  return useMutation({
    mutationFn: async (avatarUrl: string | null) => {
      logger.info(COMPONENT, 'Starting profile picture update', {
        operation: 'updateProfilePicture',
        context: {
          userId,
          avatarUrl,
          userRole,
          timestamp: new Date().toISOString()
        }
      });

      if (!userId) throw new Error("No user ID provided");

      // Check if user has permission (own profile or admin)
      const isAdmin = await hasRole('sys_admin');
      const isOwnProfile = userId === (await supabase.auth.getUser()).data.user?.id;

      if (!isAdmin && !isOwnProfile) {
        const error = new Error("You don't have permission to update this profile picture");
        logger.error(COMPONENT, 'Permission denied', {
          userRole,
          userId,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        logger.error(COMPONENT, 'Operation failed', {
          error: error.message,
          details: error,
          context: {
            userId,
            avatarUrl,
            userRole,
            timestamp: new Date().toISOString()
          }
        });
        throw error;
      }

      return data;
    },
    onMutate: async (newAvatarUrl) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ProfileData>(queryKey);

      queryClient.setQueryData<ProfileData>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          avatar_url: newAvatarUrl
        };
      });

      return { previousData, queryKey } as ProfilePictureMutationContext;
    },
    onError: (error, _, context) => {
      logger.error(COMPONENT, 'Operation failed', {
        error: error.message,
        context: {
          userId,
          timestamp: new Date().toISOString()
        }
      });

      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      toast.error(error.message || "Failed to update profile picture");
    },
    onSuccess: (data) => {
      toast.success("Profile picture updated successfully");
      queryClient.invalidateQueries({ queryKey });
    }
  });
}
